import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { ProductionPlannerState, ProductionPlannerStore } from './production-planner.store';

export const DAY_WIDTH = 34;
export const LABEL_WIDTH = 200;

@Component({
  selector: 'production-planner',
  templateUrl: './production-planner.page.html',
  styleUrl: './production-planner.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe]
})
export class ProductionPlannerPage implements OnInit, OnDestroy {
  readonly state$: Observable<ProductionPlannerState>;

  readonly DAY_WIDTH = DAY_WIDTH;
  readonly LABEL_WIDTH = LABEL_WIDTH;

  @ViewChild('plannerScroll') plannerScroll?: ElementRef<HTMLElement>;
  @ViewChild('plannerBody') plannerBody?: ElementRef<HTMLElement>;

  private dragging = false;
  private dragStartX = 0;

  constructor(
    private store: ProductionPlannerStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;
  }

  ngOnInit(): void {
    this.store.init()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => requestAnimationFrame(() => this.scrollToToday()));
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  scrollToToday(): void {
    const scroll = this.plannerScroll?.nativeElement;

    if (!scroll) {
      return;
    }

    const todayOffset = this.store.state.todayOffset;
    scroll.scrollLeft = Math.max(0, (todayOffset - 5) * DAY_WIDTH);
  }

  onBarPointerDown(event: PointerEvent, orderId: number): void {
    this.startDrag(event);
    this.store.dragStart({ kind: 'move', orderId, dayDelta: 0 });
  }

  onHandlePointerDown(event: PointerEvent, orderId: number, edge: 'start' | 'end'): void {
    event.stopPropagation();
    this.startDrag(event);
    this.store.dragStart(edge === 'start'
      ? { kind: 'resizeStart', orderId, dayDelta: 0 }
      : { kind: 'resizeEnd', orderId, dayDelta: 0 });
  }

  onTrayPointerDown(event: PointerEvent, orderId: number): void {
    this.startDrag(event);
    this.store.dragStart({ kind: 'fromTray', orderId, overOffset: null });
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.dragging) {
      return;
    }

    const drag = this.store.state.drag;

    if (!drag) {
      return;
    }

    if (drag.kind === 'fromTray') {
      this.store.trayDragMoved(this.timelineOffsetAt(event));
    } else {
      this.store.dragMovedBy(Math.round((event.clientX - this.dragStartX) / DAY_WIDTH));
    }
  }

  onPointerUp(): void {
    if (!this.dragging) {
      return;
    }

    this.dragging = false;
    this.store.dragCommitted();
  }

  onPointerCancel(): void {
    if (!this.dragging) {
      return;
    }

    this.dragging = false;
    this.store.dragCancelled();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.dragging = false;
    this.store.dragCancelled();
  }

  stop(event: Event): void {
    event.stopPropagation();
  }

  unplan(event: Event, orderId: number): void {
    event.stopPropagation();
    this.store.unplanOrder(orderId);
  }

  private startDrag(event: PointerEvent): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.dragging = true;
    this.dragStartX = event.clientX;
  }

  private timelineOffsetAt(event: PointerEvent): number | null {
    const body = this.plannerBody?.nativeElement;

    if (!body) {
      return null;
    }

    const rect = body.getBoundingClientRect();

    if (event.clientY < rect.top || event.clientY > rect.bottom) {
      return null;
    }

    const offset = Math.floor((event.clientX - rect.left - LABEL_WIDTH) / DAY_WIDTH);

    if (offset < 0 || offset >= this.store.state.dayCount) {
      return null;
    }

    return offset;
  }
}
